/* eslint-disable camelcase */
import styles from './styles';
import PropTypes from 'prop-types';
import CanvasJSReact from '../ui/canvasjs/canvasjs.react';
import { Box, Dialog, Divider, Grid, IconButton, Typography } from '@mui/material';
import { CancelPresentationRounded } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { COLORS } from '../../shared/config/colors';
import { isMobile } from 'react-device-detect';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const RheologyChartModal = ({
  onCloseClick,
  isOpen,
  wellName,
  fluidName,
  graphData
}) => {
  const classes = styles();
  const drillFluid = useSelector((state) => state.wellsDrillFluid);
  const rheoModels = graphData[0]?.rheo_data?.shear_str || {};
  const fannStress = graphData[0]?.rheo_data?.fann_s_stress || [];
  const fannRate = graphData[0]?.rheo_data?.fann_s_rate || [];
  const best = graphData[0]?.best_rheo_model || {};

  const modelStyles = {
    ypl: { label: 'YPL (Herschel Bulkley)', color: COLORS.LIME_GREEN },
    api_rp_ypl_fit: { label: 'API-RP-YPL (Curve Fit)', color: COLORS.DARK_ORANGE },
    api_rp_ypl: { label: 'API-RP-YPL (2023)', color: COLORS.YELLOW_GREEN },
    pl: { label: 'Power Law (PL)', color: COLORS.VIVID_CYAN },
    bingham: { label: 'Bingham Plastic', color: COLORS.MUTED_YELLOW },
    newtonian: { label: 'Newtonian', color: COLORS.DEEP_BLUE },
  };

  const modelOrder = [
    'ypl',
    'api_rp_ypl_fit',
    'api_rp_ypl',
    'pl',
    'bingham',
    'newtonian'
  ];

  const modelData = modelOrder
    .filter((key) => rheoModels[key])
    .map((modelKey) => {
      const style = modelStyles[modelKey] || { label: modelKey, color: 'red' };
      const label = best === modelKey ? `${style.label} **` : style.label;
      const lineThickness = best === modelKey ? 3 : 2;

      return {
        type: 'line',
        name: label,
        showInLegend: true,
        lineThickness,
        color: style.color,
        markerColor: style.color,
        lineColor: style.color,
        toolTipContent: `<b>${label}</b><br/>Shear Rate: {x} (l/s) <br/>Shear Stress: {y} ${drillFluid?.details?.units?.yp?.label ?? 'lb/100ft²'}`,
        dataPoints: fannRate.map((xVal, i) => ({
          x: Number(xVal),
          y: parseFloat(
            Number(rheoModels[modelKey][i]).toFixed(
              drillFluid?.details?.units?.yp?.decimals || 2
            )
          ),
        })),
      };
    });

  const options = {
    height: isMobile ? 500 : 600,
    animationEnabled: !isMobile,
    zoomEnabled: true,
    exportEnabled: true,
    zoomType: 'y',
    theme: 'light2',
    backgroundColor: '#FFF',
    legend: {
      fontSize: isMobile ? 12 : 18,
      cursor: 'pointer',
      horizontalAlign: 'left',
      verticalAlign: 'top',
      dockInsidePlotArea: true,
      maxWidth: isMobile ? 150 : 240,
    },
    axisX: {
      title: 'Shear Rate (l/s)',
      titleFontSize: 18,
      labelFontSize: 14,
      gridThickness: 1,
      minimum: 0,
    },
    axisY: {
      title: `Shear Stress (${drillFluid?.details?.units?.yp?.label ?? 'lb/100ft²'})`,
      titleFontSize: 18,
      labelFontSize: 14,
      gridThickness: 1,
      includeZero: true,
    },
    data: [
      {
        type: 'line',
        name: 'Fann Data',
        showInLegend: true,
        lineThickness: 2,
        lineColor: '#0D0D0E',
        markerType: 'square',
        markerSize: 6,
        markerColor: '#0D0D0E',
        toolTipContent: `<b>{name}</b><br/>Shear Rate: {x} (l/s) <br/> Shear Stress: {y} ${drillFluid?.details?.units?.yp?.label}`,
        dataPoints: fannRate
          .map((xVal, idx) => ({
            x: Number(xVal),
            y:
              fannStress[idx] != null
                ? parseFloat(
                  Number(fannStress[idx]).toFixed(
                    drillFluid?.details?.units?.yp?.decimals || 2
                  )
                )
                : null,
          }))
          .filter((p) => p.y !== null && !isNaN(p.y)),
      },
      ...modelData,
    ],
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseClick}
      maxWidth={'lg'}
      fullWidth={true}
      fullScreen={isMobile}
      style={{ marginRight: !isMobile && '20px' }}
      PaperProps={{ sx: { overflow: 'visible' } }}
      className={isMobile ? '' : classes.dialogModal}
    >
      <Box className={classes.dialogTitle}>
        <Typography variant="subtitle1" className={classes.titleHeader}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              {isMobile ? 'Mud Rheology' : [wellName, fluidName].filter(Boolean).join(' | ')}
            </Grid>
            <Grid item>
              <IconButton onClick={onCloseClick}>
                <CancelPresentationRounded fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>
        </Typography>
      </Box>
      <Divider />
      {!isMobile && <Typography
        variant="h5"
        align="center"
        sx={{ mt: 3, fontWeight: 'bold' }}
      >
        Mud Rheology
      </Typography>}
      <Box className={classes.boxStyle}>
        <Grid container marginBottom={'2%'}>
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              pt: 7,
              '.canvasjs-chart-container canvas': {},
            }}
          >
            <CanvasJSChart options={options} />
            <Box>
              <Box sx={{ textAlign: 'left', mt: 1, ml: 8, fontSize: '19px' }}>
                ** Best fit for rheology models
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

RheologyChartModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  fluidName: PropTypes.any,
  wellName: PropTypes.any,
  graphData: PropTypes.any,
};
RheologyChartModal.defaultProps = {
  selectedJob: {},
};

export default RheologyChartModal;
